import {
  Button,
  Popper,
  FormControl,
  RadioGroup,
  FormControlLabel,
  Radio,
  ClickAwayListener,
  Grow,
  Paper,
  Box,
  FormLabel,
} from '@mui/material';
import { useState, useRef, Dispatch, SetStateAction, ReactElement } from 'react';
import SortAscIcon from 'remixicon-react/SortAscIcon';
import { styled } from '@mui/material/styles';

export type SortMenuOptionType = {
  label: string;
  value: string | number;
};

export type SortMenuType = {
  options: SortMenuOptionType[];
  selectedOption: SortMenuOptionType;
};

type SortProps = {
  icon?: ReactElement;
  label?: string;
  menu: SortMenuType;
  setMenu: Dispatch<SetStateAction<SortMenuType>>;
};

function Sort({ icon, label, menu, setMenu }: SortProps) {
  const [sortOpen, setSortOpen] = useState(false);
  const sortMenuRef = useRef<HTMLButtonElement>(null);

  const handleChange = (selectedOption: SortMenuOptionType) => {
    setMenu((prevState) => ({
      ...prevState,
      selectedOption,
    }));
    setSortOpen(false);
  };

  return (
    <>
      <Button
        ref={sortMenuRef}
        onClick={() => setSortOpen(true)}
        startIcon={icon || <SortAscIcon />}
      >
        {menu.selectedOption.label}
      </Button>
      <Popper
        sx={{
          zIndex: 9,
        }}
        open={sortOpen}
        anchorEl={sortMenuRef.current}
        transition
        placement="bottom-end"
      >
        {({ TransitionProps, placement }) => (
          <Grow
            {...TransitionProps}
            style={{
              transformOrigin: placement === 'bottom' ? 'center top' : 'center bottom',
            }}
          >
            <$Paper>
              <ClickAwayListener onClickAway={() => setSortOpen(false)}>
                <FormControl sx={label ? { px: 2.5, py: 2 } : { p: 1 }}>
                  {label && (
                    <FormLabel
                      sx={(theme) => ({
                        color: theme.palette.custom.tuna,
                        fontSize: 14,
                        mb: 0.5,
                        fontWeight: 600,
                      })}
                    >
                      {label}
                    </FormLabel>
                  )}
                  <RadioGroup
                    value={menu.selectedOption.value}
                    defaultValue={menu.selectedOption.value}
                    name="sortBy"
                    sx={{ ml: label ? -1.5 : 0 }}
                  >
                    {menu.options.map((item, index) => (
                      <Box key={`${index}${item.label}`}>
                        <$FormControlLabel
                          onChange={handleChange.bind(null, item)}
                          value={item.value}
                          control={<Radio size="small" />}
                          label={item.label}
                        />
                      </Box>
                    ))}
                  </RadioGroup>
                </FormControl>
              </ClickAwayListener>
            </$Paper>
          </Grow>
        )}
      </Popper>
    </>
  );
}

const $Paper = styled(Paper, { label: 'Paper' })(({ theme }) => ({
  borderRadius: '8px',
  boxShadow: `0px 8px 16px ${theme.palette.custom.smoky}`,
}));

const $FormControlLabel = styled(FormControlLabel, { label: 'FormControlLabel' })({
  marginLeft: 0,
  '& .MuiFormControlLabel-label': {
    fontSize: 14,
  },
});

export default Sort;
